import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const SITES = [
  { id: 'plant-1', name: 'Plant A — Manchester', coordinator: 'David Brown' },
  { id: 'plant-2', name: 'Plant B — Birmingham', coordinator: 'Maria Garcia' },
  { id: 'plant-3', name: 'Plant C — Leeds', coordinator: 'James Wilson' },
  { id: 'warehouse-1', name: 'Warehouse — Sheffield', coordinator: 'Emily Rodriguez' },
  { id: 'office-1', name: 'Head Office — London', coordinator: 'Robert Thompson' },
];

// Mock cross-location tracking data
const INITIAL_CROSS_LOCATION_DATA = [
  {
    id: 'cl-1',
    recommendationId: 1,
    incidentId: 'INC-2025-001',
    recommendationText: 'Install additional chemical containment barriers in Production Area 2',
    priority: 'high',
    originSite: 'Plant A — Manchester',
    sites: [
      { siteId: 'plant-2', siteName: 'Plant B — Birmingham', coordinator: 'Maria Garcia', applicability: 'applicable', implementation: 'in_progress', dueDate: '2025-03-01', notes: 'Barriers ordered, installation scheduled' },
      { siteId: 'plant-3', siteName: 'Plant C — Leeds', coordinator: 'James Wilson', applicability: 'not_reviewed', implementation: 'not_started', dueDate: '2025-03-15', notes: '' },
      { siteId: 'warehouse-1', siteName: 'Warehouse — Sheffield', coordinator: 'Emily Rodriguez', applicability: 'not_applicable', implementation: 'not_started', dueDate: '', notes: 'No chemical storage in warehouse' },
    ],
    sharedAt: '2025-01-12T09:00:00',
  },
  {
    id: 'cl-2',
    recommendationId: 2,
    incidentId: 'INC-2025-001',
    recommendationText: 'Conduct mandatory chemical handling refresher training for all operators',
    priority: 'high',
    originSite: 'Plant A — Manchester',
    sites: [
      { siteId: 'plant-2', siteName: 'Plant B — Birmingham', coordinator: 'Maria Garcia', applicability: 'applicable', implementation: 'implemented', dueDate: '2025-02-01', notes: 'Training completed for all 42 operators' },
      { siteId: 'plant-3', siteName: 'Plant C — Leeds', coordinator: 'James Wilson', applicability: 'applicable', implementation: 'in_progress', dueDate: '2025-02-15', notes: 'Training sessions scheduled' },
    ],
    sharedAt: '2025-01-12T09:30:00',
  },
  {
    id: 'cl-3',
    recommendationId: 4,
    incidentId: 'INC-2025-002',
    recommendationText: 'Install convex mirrors at warehouse blind corners',
    priority: 'medium',
    originSite: 'Warehouse — Sheffield',
    sites: [
      { siteId: 'plant-1', siteName: 'Plant A — Manchester', coordinator: 'David Brown', applicability: 'applicable', implementation: 'implemented', dueDate: '2025-01-25', notes: 'Mirrors installed at 4 blind corners' },
      { siteId: 'plant-2', siteName: 'Plant B — Birmingham', coordinator: 'Maria Garcia', applicability: 'not_reviewed', implementation: 'not_started', dueDate: '2025-02-10', notes: '' },
    ],
    sharedAt: '2025-01-05T11:00:00',
  },
];

const implementationColors = {
  not_started: 'bg-slate-100 text-slate-600',
  in_progress: 'bg-amber-100 text-amber-700',
  implemented: 'bg-teal-100 text-teal-700',
  verified: 'bg-emerald-100 text-emerald-700',
};

const implementationLabels = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  implemented: 'Implemented',
  verified: 'Verified',
};

const applicabilityColors = {
  not_reviewed: 'bg-slate-100 text-slate-600',
  applicable: 'bg-emerald-100 text-emerald-700',
  not_applicable: 'bg-red-100 text-red-700',
};

const applicabilityLabels = {
  not_reviewed: 'Not Reviewed',
  applicable: 'Applicable',
  not_applicable: 'Not Applicable',
};

const priorityColors = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-amber-100 text-amber-700',
  low: 'bg-blue-100 text-blue-700',
};

const CrossLocationTracker = () => {
  const [data, setData] = useState(INITIAL_CROSS_LOCATION_DATA);
  const [expandedRec, setExpandedRec] = useState(null);
  const [filterSite, setFilterSite] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // { recId, siteId, field }
  const [editValue, setEditValue] = useState('');

  const filteredData = data?.filter(rec => {
    if (filterSite !== 'all') {
      if (!rec?.sites?.some(s => s?.siteId === filterSite)) return false;
    }
    if (filterStatus !== 'all') {
      if (!rec?.sites?.some(s => s?.implementation === filterStatus)) return false;
    }
    return true;
  });

  const overallStats = {
    total: data?.reduce((acc, r) => acc + r?.sites?.length, 0),
    implemented: data?.reduce((acc, r) => acc + r?.sites?.filter(s => s?.implementation === 'implemented' || s?.implementation === 'verified')?.length, 0),
    inProgress: data?.reduce((acc, r) => acc + r?.sites?.filter(s => s?.implementation === 'in_progress')?.length, 0),
    notStarted: data?.reduce((acc, r) => acc + r?.sites?.filter(s => s?.implementation === 'not_started' && s?.applicability !== 'not_applicable')?.length, 0),
    notApplicable: data?.reduce((acc, r) => acc + r?.sites?.filter(s => s?.applicability === 'not_applicable')?.length, 0),
  };

  const handleUpdateSite = (recId, siteId, field, value) => {
    setData(prev => prev?.map(rec => {
      if (rec?.id !== recId) return rec;
      return {
        ...rec,
        sites: rec?.sites?.map(s => {
          if (s?.siteId !== siteId) return s;
          return { ...s, [field]: value };
        }),
      };
    }));
    setEditingCell(null);
  };

  return (
    <div className="space-y-6">
      {/* Summary KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Site Assignments', value: overallStats?.total, icon: 'Globe', color: 'text-blue-600 bg-blue-50' },
          { label: 'Implemented / Verified', value: overallStats?.implemented, icon: 'CheckCircle', color: 'text-emerald-600 bg-emerald-50' },
          { label: 'In Progress', value: overallStats?.inProgress, icon: 'Clock', color: 'text-amber-600 bg-amber-50' },
          { label: 'Pending Action', value: overallStats?.notStarted, icon: 'AlertCircle', color: 'text-red-600 bg-red-50' },
        ]?.map(stat => (
          <div key={stat?.label} className="bg-card border border-border rounded-lg p-4 flex items-center gap-3">
            <div className={`p-2 rounded-lg ${stat?.color}`}>
              <Icon name={stat?.icon} size={18} className={stat?.color?.split(' ')?.[0]} />
            </div>
            <div>
              <p className="text-2xl font-bold text-foreground">{stat?.value}</p>
              <p className="text-xs text-muted-foreground">{stat?.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="flex items-center gap-2">
          <Icon name="Filter" size={16} className="text-muted-foreground" />
          <span className="text-sm text-muted-foreground font-medium">Filter:</span>
        </div>
        <select
          value={filterSite}
          onChange={e => setFilterSite(e?.target?.value)}
          className="text-sm px-3 py-1.5 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
          <option value="all">All Sites</option>
          {SITES?.map(s => <option key={s?.id} value={s?.id}>{s?.name}</option>)}
        </select>
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e?.target?.value)}
          className="text-sm px-3 py-1.5 border border-border rounded-lg bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
        >
          <option value="all">All Statuses</option>
          <option value="not_started">Not Started</option>
          <option value="in_progress">In Progress</option>
          <option value="implemented">Implemented</option>
          <option value="verified">Verified</option>
        </select>
        <span className="text-xs text-muted-foreground ml-auto">{filteredData?.length} recommendation{filteredData?.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Recommendation Cards */}
      <div className="space-y-4">
        {filteredData?.length === 0 ? (
          <div className="bg-card border border-border rounded-lg p-12 text-center">
            <Icon name="Globe" size={48} className="text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No cross-location recommendations found</p>
          </div>
        ) : (
          filteredData?.map(rec => {
            const isExpanded = expandedRec === rec?.id;
            const implementedCount = rec?.sites?.filter(s => s?.implementation === 'implemented' || s?.implementation === 'verified')?.length;
            const totalApplicable = rec?.sites?.filter(s => s?.applicability !== 'not_applicable')?.length;

            return (
              <div key={rec?.id} className="bg-card border border-border rounded-xl overflow-hidden shadow-sm">
                {/* Header */}
                <button
                  type="button"
                  onClick={() => setExpandedRec(isExpanded ? null : rec?.id)}
                  className="w-full flex items-start gap-4 p-5 text-left hover:bg-muted/30 transition-colors"
                >
                  <div className="p-2 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex-shrink-0 mt-0.5">
                    <Icon name="Globe" size={16} className="text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="font-mono text-xs font-semibold text-primary">{rec?.incidentId}</span>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${priorityColors?.[rec?.priority] || 'bg-gray-100 text-gray-700'}`}>
                        {rec?.priority?.toUpperCase()} priority
                      </span>
                      <span className="text-xs text-muted-foreground">Origin: {rec?.originSite}</span>
                    </div>
                    <p className="text-sm font-semibold text-foreground leading-snug mb-2">{rec?.recommendationText}</p>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1.5">
                        <div className="flex-1 w-32 bg-muted rounded-full h-1.5">
                          <div
                            className="bg-emerald-500 h-1.5 rounded-full transition-all"
                            style={{ width: totalApplicable > 0 ? `${(implementedCount / totalApplicable) * 100}%` : '0%' }}
                          />
                        </div>
                        <span className="text-xs text-muted-foreground">{implementedCount}/{totalApplicable} sites done</span>
                      </div>
                      <span className="text-xs text-muted-foreground">{rec?.sites?.length} site{rec?.sites?.length !== 1 ? 's' : ''} assigned</span>
                    </div>
                  </div>
                  <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground flex-shrink-0 mt-1" />
                </button>

                {/* Expanded Site Matrix */}
                {isExpanded && (
                  <div className="border-t border-border">
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-muted/50">
                            <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Site</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Coordinator</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Applicability</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Implementation</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Due Date</th>
                            <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">Notes</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                          {rec?.sites?.map(site => {
                            const isOverdue = site?.dueDate && new Date(site.dueDate) < new Date() && site?.implementation !== 'implemented' && site?.implementation !== 'verified';
                            return (
                              <tr key={site?.siteId} className={`hover:bg-muted/20 transition-colors ${isOverdue ? 'bg-red-50/50 dark:bg-red-900/10' : ''}`}>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-1.5">
                                    {isOverdue && <Icon name="AlertCircle" size={12} className="text-red-500 flex-shrink-0" />}
                                    <span className="font-medium text-foreground">{site?.siteName}</span>
                                  </div>
                                </td>
                                <td className="px-4 py-3 text-muted-foreground">{site?.coordinator}</td>
                                <td className="px-4 py-3">
                                  <select
                                    value={site?.applicability}
                                    onChange={e => handleUpdateSite(rec?.id, site?.siteId, 'applicability', e?.target?.value)}
                                    className={`text-xs px-2 py-1 rounded-full font-medium border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/30 ${applicabilityColors?.[site?.applicability] || 'bg-slate-100 text-slate-600'}`}
                                  >
                                    <option value="not_reviewed">Not Reviewed</option>
                                    <option value="applicable">Applicable</option>
                                    <option value="not_applicable">Not Applicable</option>
                                  </select>
                                </td>
                                <td className="px-4 py-3">
                                  {site?.applicability === 'not_applicable' ? (
                                    <span className="text-xs text-muted-foreground italic">N/A</span>
                                  ) : (
                                    <select
                                      value={site?.implementation}
                                      onChange={e => handleUpdateSite(rec?.id, site?.siteId, 'implementation', e?.target?.value)}
                                      className={`text-xs px-2 py-1 rounded-full font-medium border-0 cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/30 ${implementationColors?.[site?.implementation] || 'bg-slate-100 text-slate-600'}`}
                                    >
                                      <option value="not_started">Not Started</option>
                                      <option value="in_progress">In Progress</option>
                                      <option value="implemented">Implemented</option>
                                      <option value="verified">Verified</option>
                                    </select>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  {editingCell?.recId === rec?.id && editingCell?.siteId === site?.siteId && editingCell?.field === 'dueDate' ? (
                                    <input
                                      type="date"
                                      autoFocus
                                      defaultValue={site?.dueDate}
                                      onBlur={e => handleUpdateSite(rec?.id, site?.siteId, 'dueDate', e?.target?.value)}
                                      className="text-xs px-2 py-1 border border-primary rounded focus:outline-none"
                                    />
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => setEditingCell({ recId: rec?.id, siteId: site?.siteId, field: 'dueDate' })}
                                      className={`text-xs hover:underline ${isOverdue ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`}
                                    >
                                      {site?.dueDate ? new Date(site.dueDate)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '— set date'}
                                    </button>
                                  )}
                                </td>
                                <td className="px-4 py-3">
                                  {editingCell?.recId === rec?.id && editingCell?.siteId === site?.siteId && editingCell?.field === 'notes' ? (
                                    <input
                                      type="text"
                                      autoFocus
                                      defaultValue={site?.notes}
                                      onBlur={e => handleUpdateSite(rec?.id, site?.siteId, 'notes', e?.target?.value)}
                                      className="text-xs px-2 py-1 border border-primary rounded w-full focus:outline-none"
                                      placeholder="Add notes..."
                                    />
                                  ) : (
                                    <button
                                      type="button"
                                      onClick={() => setEditingCell({ recId: rec?.id, siteId: site?.siteId, field: 'notes' })}
                                      className="text-xs text-muted-foreground hover:text-foreground text-left max-w-xs truncate"
                                    >
                                      {site?.notes || <span className="italic text-muted-foreground/60">— add notes</span>}
                                    </button>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default CrossLocationTracker;
